import { createAuth, PRODUCT_NAME, wakeDatabase, watchForStaleDeploy } from "@staffsmith/web-auth";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { ApiError, createApi } from "./api.js";
import { loadConfig } from "./config.js";
import { Services } from "./context.js";
import { routes } from "./routes.js";
import "./styles.css";

const root = createRoot(document.getElementById("root") as HTMLElement);
document.title = `${PRODUCT_NAME} operators`;
// A preload left behind by a deploy reloads once, then shows the "new version" banner (C1).
watchForStaleDeploy();

try {
  const config = await loadConfig();
  // Starts a paused database resuming while the sign-in page loads; never waited on.
  wakeDatabase(config.apiUrl);
  const auth = createAuth({
    region: config.cognitoRegion,
    clientId: config.cognitoClientId,
    storageKey: "staffsmith.operator.tokens",
  });
  const api = createApi(config.apiUrl, auth);
  const queries = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Retrying a refusal won't change the answer; retry only network and server errors.
        retry: (count, error) => count < 2 && !(error instanceof ApiError && error.status < 500),
      },
    },
  });
  root.render(
    <StrictMode>
      <Services.Provider value={{ api, auth, staffAppUrl: config.staffAppUrl }}>
        <QueryClientProvider client={queries}>
          <RouterProvider router={createBrowserRouter(routes)} />
        </QueryClientProvider>
      </Services.Provider>
    </StrictMode>,
  );
} catch (e) {
  root.render(
    <main className="page">
      <h1>The operator app can't start</h1>
      <p className="error">{e instanceof Error ? e.message : String(e)}</p>
    </main>,
  );
}
